/**
 * Storm animated line icons (protect / serve / explore).
 *
 * Canvas-based dot-matrix + stroke-draw animation. The icon animates in when
 * the closest trigger element (or the canvas itself) is hovered, and retracts
 * on mouse leave. Colors follow the surrounding color scheme via currentColor.
 *
 * Markup: <canvas class="storm-icon" data-shape="protect"></canvas>
 * Optional trigger: wrap in an element matching [data-icon-trigger] or an <a>.
 */
(() => {
  const SIZE = 90;

  function cubicBezier(p1x, p1y, p2x, p2y) {
    const A = (a1, a2) => 1 - 3 * a2 + 3 * a1;
    const B = (a1, a2) => 3 * a2 - 6 * a1;
    const C = (a1) => 3 * a1;
    const calc = (t, a1, a2) => ((A(a1, a2) * t + B(a1, a2)) * t + C(a1)) * t;
    const slope = (t, a1, a2) => 3 * A(a1, a2) * t * t + 2 * B(a1, a2) * t + C(a1);
    const getTforX = (x) => {
      let t = x;
      for (let i = 0; i < 8; i++) {
        const s = slope(t, p1x, p2x);
        if (s === 0) break;
        t -= (calc(t, p1x, p2x) - x) / s;
      }
      return t;
    };
    return (t) => (t <= 0 ? 0 : t >= 1 ? 1 : calc(getTforX(t), p1y, p2y));
  }

  const easeStroke = cubicBezier(0.6, 1, 0.3, 1);
  const easeRetract = cubicBezier(0.4, 0, 1, 1);

  function insetTriangle(pts, d) {
    const norm = (a, b) => {
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const len = Math.sqrt(dx * dx + dy * dy);
      return [-dy / len, dx / len];
    };
    const lineIntersect = (p1, n1, p2, n2) => {
      const t1 = [p1[0] + d * n1[0], p1[1] + d * n1[1]];
      const t2 = [p2[0] + d * n2[0], p2[1] + d * n2[1]];
      const d1 = [n1[1], -n1[0]];
      const d2 = [n2[1], -n2[0]];
      const dx = t2[0] - t1[0];
      const dy = t2[1] - t1[1];
      const cross = d1[0] * d2[1] - d1[1] * d2[0];
      if (Math.abs(cross) < 1e-10) return t1;
      const t = (dx * d2[1] - dy * d2[0]) / cross;
      return [t1[0] + t * d1[0], t1[1] + t * d1[1]];
    };
    const n = pts.length;
    const normals = pts.map((_, i) => norm(pts[i], pts[(i + 1) % n]));
    let area = 0;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += pts[i][0] * pts[j][1] - pts[j][0] * pts[i][1];
    }
    const sign = area > 0 ? 1 : -1;
    const inNormals = normals.map((nm) => [nm[0] * sign, nm[1] * sign]);
    return pts.map((_, i) => lineIntersect(pts[i], inNormals[(i + n - 1) % n], pts[i], inNormals[i]));
  }

  function triPerim(pts) {
    let s = 0;
    for (let i = 0; i < pts.length; i++) {
      const j = (i + 1) % pts.length;
      const dx = pts[j][0] - pts[i][0];
      const dy = pts[j][1] - pts[i][1];
      s += Math.sqrt(dx * dx + dy * dy);
    }
    return s;
  }

  const outerTri = [[45, 4], [88, 80], [2, 80]];
  const innerTri = insetTriangle(outerTri, 13);

  const SHAPES = {
    protect: {
      drawOuter(c) {
        c.moveTo(45, 4);
        c.lineTo(88, 80);
        c.lineTo(2, 80);
        c.closePath();
      },
      drawInner(c) {
        c.moveTo(innerTri[0][0], innerTri[0][1]);
        c.lineTo(innerTri[1][0], innerTri[1][1]);
        c.lineTo(innerTri[2][0], innerTri[2][1]);
        c.closePath();
      },
      outerLen: Math.ceil(triPerim(outerTri)),
      innerLen: Math.ceil(triPerim(innerTri)),
    },
    serve: {
      drawOuter(c) {
        c.rect(12, 12, 66, 66);
      },
      drawInner(c) {
        c.rect(26, 26, 38, 38);
      },
      outerLen: 264,
      innerLen: 152,
    },
    explore: {
      drawOuter(c) {
        c.arc(45, 45, 37, -Math.PI / 2, 3 * Math.PI / 2);
      },
      drawInner(c) {
        c.arc(45, 45, 23, -Math.PI / 2, 3 * Math.PI / 2);
      },
      outerLen: 233,
      innerLen: 145,
    },
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initIcon(canvas) {
    const shape = SHAPES[canvas.dataset.shape];
    if (!shape || canvas.dataset.iconInit) return;
    canvas.dataset.iconInit = 'true';

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = SIZE * dpr;
    canvas.height = SIZE * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const color = getComputedStyle(canvas).color;

    // Sample the filled outer shape into a dot grid
    const off = document.createElement('canvas');
    off.width = SIZE;
    off.height = SIZE;
    const octx = off.getContext('2d');
    octx.beginPath();
    shape.drawOuter(octx);
    octx.fillStyle = '#fff';
    octx.fill();
    const data = octx.getImageData(0, 0, SIZE, SIZE).data;
    const dots = [];
    const sp = 7;
    for (let y = sp / 2; y < SIZE; y += sp) {
      for (let x = sp / 2; x < SIZE; x += sp) {
        if (data[(Math.floor(y) * SIZE + Math.floor(x)) * 4 + 3] > 128) {
          dots.push({ x, y, t: 0.35 + Math.random() * 0.6 });
        }
      }
    }

    let progress = 0;
    let animStart = null;
    let animDir = 1;
    const DUR_IN = 1100;
    const DUR_OUT = 600;
    const INNER_DELAY = 0.2;

    function draw(ts) {
      if (animStart !== null) {
        const elapsed = ts - animStart;
        const dur = animDir === 1 ? DUR_IN : DUR_OUT;
        const raw = Math.min(1, elapsed / dur);
        const e = animDir === 1 ? easeStroke(raw) : easeRetract(raw);
        progress = animDir === 1 ? e : 1 - e;
        if (raw >= 1) animStart = null;
      }
      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.fillStyle = color;
      dots.forEach((d) => {
        const alpha = Math.max(0, Math.min(1, (d.t - progress) / 0.1)) * 0.55;
        if (alpha <= 0) return;
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(d.x, d.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      ctx.strokeStyle = color;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const od = shape.outerLen * progress;
      if (od > 0.5) {
        ctx.setLineDash([od, shape.outerLen + 10]);
        ctx.lineWidth = 2;
        ctx.beginPath();
        shape.drawOuter(ctx);
        ctx.stroke();
      }
      const ip = Math.max(0, (progress - INNER_DELAY) / (1 - INNER_DELAY));
      const id2 = shape.innerLen * ip;
      if (id2 > 0.5) {
        ctx.setLineDash([id2, shape.innerLen + 10]);
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        shape.drawInner(ctx);
        ctx.stroke();
      }
      ctx.setLineDash([]);
      if (animStart !== null) requestAnimationFrame(draw);
    }

    function animateTo(dir) {
      if (reducedMotion.matches) {
        progress = dir === 1 ? 1 : 0;
        animStart = null;
        requestAnimationFrame(draw);
        return;
      }
      animDir = dir;
      requestAnimationFrame((ts) => {
        const dur = dir === 1 ? DUR_IN : DUR_OUT;
        const done = dir === 1 ? progress : 1 - progress;
        animStart = ts - done * dur * 0.05;
        requestAnimationFrame(draw);
      });
    }

    const trigger = canvas.closest('[data-icon-trigger]') || canvas.closest('a') || canvas;
    trigger.addEventListener('mouseenter', () => animateTo(1));
    trigger.addEventListener('mouseleave', () => animateTo(-1));

    requestAnimationFrame(draw);
  }

  function initAll() {
    document.querySelectorAll('canvas.storm-icon').forEach(initIcon);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when sections are re-rendered in the theme editor
  document.addEventListener('shopify:section:load', initAll);
})();
